import axios from 'axios';
import { API_URL } from '../config';
import type { User, UserListResponse } from '../types';

/**
 * Busca usuários ativos via GET /api/users/search (paginado no servidor).
 * Use `term` vazio para os primeiros `limit` resultados ordenados por nome (útil para testes; o fluxo de solicitante envia termo ao digitar).
 */
export async function searchActiveUsers(
  term: string,
  token: string | null,
  options?: { limit?: number; signal?: AbortSignal }
): Promise<User[]> {
  const limit = options?.limit ?? 25;
  const params: Record<string, string | number | boolean | undefined> = {
    limit,
    sort_by: 'name',
    sort_order: 'asc',
    is_active: true,
  };
  const t = term.trim();
  if (t.length > 0) {
    params.search = t;
  }
  const response = await axios.get<UserListResponse>(`${API_URL}/api/users/search`, {
    params,
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    signal: options?.signal,
  });
  return response.data.items;
}

/** Carrega todos os usuários ativos paginando em lotes de até 100 (limite da API). */
export async function fetchAllActiveUsers(
  token: string | null,
  options?: { signal?: AbortSignal },
): Promise<User[]> {
  const pageSize = 100;
  const all: User[] = [];
  let skip = 0;
  for (;;) {
    const response = await axios.get<UserListResponse>(`${API_URL}/api/users/search`, {
      params: {
        skip,
        limit: pageSize,
        sort_by: 'name',
        sort_order: 'asc',
        is_active: true,
      },
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      signal: options?.signal,
    });
    const batch = response.data.items || [];
    all.push(...batch);
    if (batch.length < pageSize) break;
    skip += pageSize;
  }
  return all;
}

export async function fetchUserById(
  userId: number,
  token: string | null,
  options?: { signal?: AbortSignal },
): Promise<User | null> {
  if (!Number.isFinite(userId) || userId <= 0) return null;
  try {
    const response = await axios.get<User>(`${API_URL}/api/users/${userId}`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      signal: options?.signal,
    });
    return response.data;
  } catch {
    return null;
  }
}

/**
 * Busca usuários ativos para formulário de requisição de compra (solicitante, etc.).
 * Usa GET /api/users/purchase-picker — acessível a qualquer usuário autenticado.
 */
export async function searchPurchaseFormUsers(
  term: string,
  token: string | null,
  options?: { limit?: number; signal?: AbortSignal }
): Promise<User[]> {
  const limit = options?.limit ?? 40;
  const t = term.trim();
  const response = await axios.get<User[]>(`${API_URL}/api/users/purchase-picker`, {
    params: { search: t, limit },
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    signal: options?.signal,
  });
  return response.data;
}

/**
 * Busca usuários ativos para solicitante/dono no formulário de mudança.
 * Usa GET /api/users/change-form-picker — qualquer autenticado, limite menor que compras.
 */
export async function searchChangeFormUsers(
  term: string,
  token: string | null,
  options?: { limit?: number; signal?: AbortSignal },
): Promise<User[]> {
  const limit = options?.limit ?? 30;
  const t = term.trim();
  const response = await axios.get<User[]>(`${API_URL}/api/users/change-form-picker`, {
    params: { search: t, limit },
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    signal: options?.signal,
  });
  return response.data;
}
